import { css, type SerializedStyles } from '@emotion/react'
import { iconButtonClasses, inputAdornmentClasses, inputLabelClasses, outlinedInputClasses } from '@mui/material'
import { type Theme } from '@mui/material/styles'
import { rem, size } from 'polished'

import { highContrast } from '@Styles/a11y'
import { RADIUS_1 } from '@Styles/constants/radius'
import { getFocusVisibleStyle } from '@Styles/focus'

export const inputWrapper = css({
  display: 'flex',
  flex: '100%',
  flexDirection: 'column',
})

export const multiline = css({
  [`.${outlinedInputClasses.multiline}`]: {
    alignItems: 'flex-start',
  },
})

export const input = (theme: Theme, isError = false, isFilled = false, disabled = false): SerializedStyles =>
  css({
    [`.${outlinedInputClasses.root}`]: highContrast({
      border: '1px solid transparent',
    }),
    [`.${inputAdornmentClasses.positionStart}`]: {
      height: rem(23),
    },
    [`.${inputAdornmentClasses.positionEnd}`]: {
      marginLeft: theme.spacing(0),
    },
    [`.${outlinedInputClasses.adornedStart}`]: {
      paddingLeft: theme.spacing(2),
    },
    [`.${outlinedInputClasses.adornedEnd}`]: {
      paddingRight: theme.spacing(0),
    },
    [`.${inputLabelClasses.root}.${inputLabelClasses.error}:not(.${inputLabelClasses.shrink})`]: {
      color: theme.palette.text.secondary,
    },
    [`.${outlinedInputClasses.notchedOutline}`]: {
      ...(!disabled && {
        border: `solid 1px ${isFilled ? theme.palette.text.primary : theme.customPalette.onElements.outline}`,
      }),
    },
    [`.${outlinedInputClasses.root}.${outlinedInputClasses.disabled} .${outlinedInputClasses.notchedOutline}`]: {
      borderColor: theme.palette.text.disabled,
    },
    background: 'inherit',
    caretColor: isError ? theme.palette.error.main : theme.palette.primary.main,
    width: '100%',
  })

export const startAdornmentTypography = css({
  lineHeight: 'initial', // Reinit the typography line-height
})

export const inputButton = (theme: Theme, disabled = false): SerializedStyles =>
  css({
    '&:hover': {
      backgroundColor: 'transparent',
    },
    [`.${iconButtonClasses.root}.${iconButtonClasses.disabled}`]: {
      color: theme.palette.text.disabled,
    },
    color: theme.palette.text[disabled ? 'disabled' : 'primary'],
    cursor: 'pointer',
  })

export const buttonFocus = (theme: Theme, isFocused = false): SerializedStyles =>
  css({
    '&::before': isFocused ? getFocusVisibleStyle(theme.palette.primary.main, RADIUS_1, '0') : {},
  })

export const inputButtonIcon = (theme: Theme, disabled = false): SerializedStyles =>
  css({
    ...size(rem(24)),
    fill: theme.palette.text[disabled ? 'disabled' : 'primary'],
  })

export const inputRules = css({
  listStyle: 'none',
  margin: 0,
  padding: 0,
})

export const inputRule = (theme: Theme): SerializedStyles =>
  css({
    '&:last-child': {
      marginBottom: 0,
    },
    display: 'flex',
    marginBottom: theme.spacing(0),
  })

export const invalidRuleLabel = (theme: Theme): SerializedStyles =>
  css({
    color: theme.palette.text.secondary,
  })

export const ruleIcon = (theme: Theme): SerializedStyles =>
  css({
    paddingRight: theme.spacing(0),
    ...size(rem(16)),
    minWidth: rem(16),
  })

export const validRuleIcon = (theme: Theme): SerializedStyles =>
  css({
    fill: theme.palette.success.main,
  })

export const invalidRuleIcon = (theme: Theme): SerializedStyles =>
  css({
    fill: theme.palette.text.secondary,
  })

export const multilist = css({
  display: 'flex',
  justifyContent: 'end',
})

export const onClearButton = (theme: Theme): SerializedStyles =>
  css({
    marginTop: `-${theme.spacing(0.5)}`,
  })
